/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.searchRequestId = 0;
      this.updateResultsHeight = this.updateResultsHeight.bind(this);
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
        return;
      }

      const matchingCollection = this.getStrongCollectionMatch(this.getQuery());
      if (matchingCollection) {
        evt.preventDefault();
        const url = new URL(theme.routes.search, window.location.origin);
        url.searchParams.set('q', PredictiveSearch.getCollectionIntentQuery(this.getQuery()));
        url.searchParams.set('display_q', this.getQuery());
        url.searchParams.set('type', 'product');
        url.searchParams.set('sort_by', 'relevance');
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('options[fields]', 'title,product_type,variants.title,vendor');
        window.location.assign(url.toString());
      }
    }

    /**
     * Finds a collection whose title contains every meaningful query concept.
     * This is deliberately limited to multi-word queries so broad, single-word
     * product searches retain Shopify's standard results page.
     * @param {string} searchTerm - Search query.
     * @returns {HTMLAnchorElement|null}
     */
    getStrongCollectionMatch(searchTerm) {
      const queryTerms = PredictiveSearch.getSearchTerms(searchTerm);
      if (queryTerms.length < 2) return null;

      const candidates = Array.from(
        this.querySelectorAll('[id$="-collections"] .predictive-search__item:not(.js-submit)')
      ).map((item, index) => {
        const link = item.querySelector('a[href]');
        const titleTerms = PredictiveSearch.getSearchTerms(item.dataset.searchTitle);
        const matches = link && queryTerms.every((term) => titleTerms.includes(term));
        return { link, index, extraTerms: titleTerms.length - queryTerms.length, matches };
      }).filter((candidate) => candidate.matches);

      candidates.sort((a, b) => a.extraTerms - b.extraTerms || a.index - b.index);
      return candidates[0]?.link || null;
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const requestId = ++this.searchRequestId;
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const fullSearchProductsPromise = this.getFullSearchProducts(searchTerm);
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const fullSearchProducts = await fullSearchProductsPromise;
        if (requestId !== this.searchRequestId) return;
        this.mergeFullSearchProducts(resultsEl, fullSearchProducts);
        this.rankProductResults(resultsEl, searchTerm);
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Gets product matches from Shopify's full search index. This supplements
     * predictive search when its narrower index omits valid model matches.
     * @param {string} searchTerm - Search query.
     * @returns {Promise<DocumentFragment|null>}
     */
    async getFullSearchProducts(searchTerm) {
      const url = new URL(theme.routes.search, window.location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('type', 'product');
      url.searchParams.set('sort_by', 'relevance');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[fields]', 'title,product_type,variants.title,vendor');
      url.searchParams.set('view', 'predictive-products');

      try {
        const response = await fetch(url.toString());
        if (!response.ok) return null;

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        return tmpl.content.querySelector('[data-full-search-products]');
      } catch (error) {
        return null;
      }
    }

    /**
     * Uses the full-search product list when it contains more valid matches
     * than Shopify's predictive response.
     * @param {Element} resultsEl - Predictive-search section wrapper.
     * @param {Element|null} fullSearchProducts - Full-search response wrapper.
     */
    mergeFullSearchProducts(resultsEl, fullSearchProducts) {
      if (!resultsEl || !fullSearchProducts) return;

      const fullPanel = fullSearchProducts.querySelector('[role="tabpanel"]');
      const fullList = fullPanel?.querySelector('.predictive-search__list');
      const fullCount = fullList?.querySelectorAll('.predictive-search__item:not(.js-submit)').length || 0;
      if (fullCount === 0) return;

      const currentPanel = resultsEl.querySelector('#psearch-products');
      const currentList = currentPanel?.querySelector('.predictive-search__list');
      const currentCount = currentList?.querySelectorAll('.predictive-search__item:not(.js-submit)').length || 0;
      if (fullCount <= currentCount) return;

      if (currentList) {
        const mergedList = fullList.cloneNode(true);
        const submitItem = mergedList.querySelector('.js-submit');
        const existingUrls = new Set(Array.from(
          mergedList.querySelectorAll('.predictive-search__item:not(.js-submit) a[href]')
        ).map((link) => link.getAttribute('href')));

        currentList.querySelectorAll('.predictive-search__item:not(.js-submit)').forEach((item) => {
          const url = item.querySelector('a[href]')?.getAttribute('href');
          const mergedCount = mergedList.querySelectorAll('.predictive-search__item:not(.js-submit)').length;
          if (!url || existingUrls.has(url) || mergedCount >= theme.settings.pSearchLimit) return;

          mergedList.insertBefore(item.cloneNode(true), submitItem);
          existingUrls.add(url);
        });

        currentList.replaceWith(mergedList);
        return;
      }

      const productButton = fullSearchProducts.querySelector('[role="tab"]')?.cloneNode(true);
      const productPanel = fullPanel.cloneNode(true);
      if (!productButton) return;

      let tabbedContent = resultsEl.querySelector('tabbed-content');
      if (!tabbedContent) {
        tabbedContent = document.createElement('tabbed-content');
        tabbedContent.className = 'grow';

        const tablist = document.createElement('div');
        tablist.className = 'tablist relative overflow-hidden bg-theme-bg mb-2';
        const tablistScroller = document.createElement('div');
        tablistScroller.className = 'tablist__scroller flex';
        tablistScroller.setAttribute('role', 'tablist');
        tablist.append(tablistScroller);
        tabbedContent.append(tablist, productPanel);
        resultsEl.querySelector('.predictive-search__results')?.append(tabbedContent);
      } else {
        tabbedContent.querySelectorAll('[role="tab"]').forEach((tab) => {
          tab.setAttribute('aria-selected', 'false');
          tab.setAttribute('tabindex', '-1');
        });
        tabbedContent.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
          panel.hidden = true;
        });
        tabbedContent.querySelector('[role="tablist"]')?.prepend(productButton);
        tabbedContent.querySelector('.tablist')?.after(productPanel);
      }

      productButton.setAttribute('aria-selected', 'true');
      productButton.removeAttribute('tabindex');
      productPanel.hidden = false;

      if (!productButton.isConnected) {
        tabbedContent.querySelector('[role="tablist"]')?.prepend(productButton);
      }
    }

    /**
     * Orders predictive products by how closely their searchable metadata matches the query.
     * Shopify still controls which products are returned; this only makes the small suggestion
     * set deterministic and keeps direct title matches ahead of broader metadata matches.
     * @param {Element} resultsEl - Predictive-search section wrapper.
     * @param {string} searchTerm - Search query.
     */
    rankProductResults(resultsEl, searchTerm) {
      const productList = resultsEl?.querySelector('#psearch-products .predictive-search__list');
      if (!productList) return;

      const submitItem = productList.querySelector('.js-submit');
      const productItems = Array.from(
        productList.querySelectorAll('.predictive-search__item:not(.js-submit)')
      );
      const normalizedQuery = PredictiveSearch.normalizeSearchText(searchTerm);
      const queryTerms = [...new Set(normalizedQuery.split(' ').filter(Boolean))];

      const score = (item) => {
        const title = PredictiveSearch.normalizeSearchText(item.dataset.searchTitle);
        const productType = PredictiveSearch.normalizeSearchText(item.dataset.searchProductType);
        const variantTitles = PredictiveSearch.normalizeSearchText(item.dataset.searchVariantTitles);
        const vendor = PredictiveSearch.normalizeSearchText(item.dataset.searchVendor);
        const countMatches = (value) => queryTerms.filter((term) => value.includes(term)).length;

        return (normalizedQuery && title.includes(normalizedQuery) ? 1000 : 0)
          + countMatches(title) * 100
          + countMatches(productType) * 20
          + countMatches(variantTitles) * 10
          + countMatches(vendor) * 5;
      };

      const rankedItems = productItems
        .map((item, index) => ({ item, index, score: score(item) }));
      const hasDirectMatches = rankedItems.some((result) => result.score > 0);

      rankedItems
        .filter((result) => {
          if (hasDirectMatches && result.score === 0) result.item.remove();
          return !hasDirectMatches || result.score > 0;
        })
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .forEach(({ item }) => productList.insertBefore(item, submitItem));
    }

    static normalizeSearchText(value) {
      return (value || '')
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    static getSearchTerms(value) {
      return [...new Set(PredictiveSearch.normalizeSearchText(value)
        .split(' ')
        .filter(Boolean)
        .map((term) => term.length > 3 && term.endsWith('s') ? term.slice(0, -1) : term))];
    }

    static getCollectionIntentQuery(value) {
      return PredictiveSearch.getSearchTerms(value)
        .map((term) => `title:${term}`)
        .join(' ');
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Keeps the mobile results scroller inside the visible viewport. The search
     * panel starts below the header, so a height based on viewport size alone
     * can put the last result below Safari's bottom toolbar.
     */
    updateResultsHeight() {
      if (!this.hasAttribute('open')) return;

      const scroller = this.results.querySelector('.predictive-search__scroller');
      if (!scroller) return;

      if (window.matchMedia('(min-width: 769px)').matches) {
        scroller.style.removeProperty('max-height');
        return;
      }

      const viewport = window.visualViewport;
      const viewportBottom = viewport
        ? viewport.offsetTop + viewport.height
        : window.innerHeight;
      const availableHeight = Math.max(0, viewportBottom - scroller.getBoundingClientRect().top - 8);
      scroller.style.maxHeight = `${availableHeight}px`;
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');
      requestAnimationFrame(this.updateResultsHeight);
      window.addEventListener('resize', this.updateResultsHeight);
      window.addEventListener('scroll', this.updateResultsHeight, { passive: true });
      window.visualViewport?.addEventListener('resize', this.updateResultsHeight);
      window.visualViewport?.addEventListener('scroll', this.updateResultsHeight);

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');
      window.removeEventListener('resize', this.updateResultsHeight);
      window.removeEventListener('scroll', this.updateResultsHeight);
      window.visualViewport?.removeEventListener('resize', this.updateResultsHeight);
      window.visualViewport?.removeEventListener('scroll', this.updateResultsHeight);

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
